import { Box, Button, Text } from "@chakra-ui/react";
import { ChevronRightIcon } from "@chakra-ui/icons";
import { useDeferredValue, useId, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ContractNature } from "../../autogen/bff-api";
import { CpvCategoryTree } from "./CpvCategoryTree";
import { CpvSearchInput } from "./CpvSearchInput";
import { CpvSearchResults } from "./CpvSearchResults";
import { CpvSelectedChips } from "./CpvSelectedChips";
import { CpvSuggestions, type CpvSuggestionContext } from "./CpvSuggestions";
import { asMainCode, everyCodeFits, fitsAsMainCode, withMainCodeFirst } from "./mainCpvCode";
import { useCpvCodes } from "./useCpvCodes";

export type CpvSelectorProps = {
  value: string[];
  onChange: (next: string[]) => void;
  isInvalid?: boolean;
  isDisabled?: boolean;
  suggestionContext?: CpvSuggestionContext;
  mainCode?: { contractNature: ContractNature | null | undefined };
};

export const CpvSelector = ({
  value,
  onChange,
  isDisabled,
  isInvalid,
  suggestionContext,
  mainCode,
}: CpvSelectorProps) => {
  const { t } = useTranslation();
  const panelId = useId();
  const { tree, codeIndex, mainCodeRules, isLoading, isError } = useCpvCodes();
  const rulesKnown = !isLoading && !isError;
  const hasMainCode = !!mainCode;
  const contractNature = mainCode?.contractNature;
  const fits = useMemo(
    () => (rulesKnown && hasMainCode ? fitsAsMainCode(mainCodeRules, contractNature) : null),
    [rulesKnown, hasMainCode, mainCodeRules, contractNature],
  );
  const onlyAdditional = fits ? (code: string) => !fits(code) : undefined;
  const change = (next: string[]) => onChange(fits ? withMainCodeFirst(next, fits) : next);

  const [query, setQuery] = useState("");
  const deferredQuery = useDeferredValue(query);
  const [isTreeOpen, setIsTreeOpen] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const browseRef = useRef<HTMLButtonElement>(null);

  const selectedSet = useMemo(() => new Set(value), [value]);

  const handleToggleSelect = (code: string): void => {
    if (selectedSet.has(code)) change(value.filter((c) => c !== code));
    else change([...value, code]);
  };

  const handleToggleExpand = (code: string): void => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  };

  const handleRemove = (code: string): void => {
    change(value.filter((c) => c !== code));
  };

  const isSearching = deferredQuery.trim() !== "";
  const isPanelOpen = isSearching || isTreeOpen;

  return (
    <Box>
      <CpvSelectedChips
        selected={value}
        codeIndex={codeIndex}
        onRemove={handleRemove}
        mainCode={
          mainCode
            ? { fits: rulesKnown ? (fits ?? everyCodeFits) : null, onMakeMain: (code) => onChange(asMainCode(value, code)) }
            : undefined
        }
      />
      {isError && (
        <Text fontSize="sm" color="smError" mb="2">
          {t("The CPV code list could not be loaded. Reload the page to try again.")}
        </Text>
      )}
      <CpvSearchInput
        value={query}
        onChange={setQuery}
        isDisabled={isDisabled || isLoading}
        isInvalid={isInvalid}
      />
      <Button
        ref={browseRef}
        mt="2"
        variant="link"
        size="sm"
        colorScheme="teal"
        leftIcon={
          <ChevronRightIcon
            transform={isPanelOpen ? "rotate(90deg)" : undefined}
            transition="transform 0.2s ease"
          />
        }
        aria-expanded={isPanelOpen}
        aria-controls={panelId}
        onClick={() => {
          setQuery("");
          setIsTreeOpen(!isPanelOpen);
        }}
        isDisabled={isDisabled || isLoading}
        alignSelf="flex-start"
      >
        {t("Browse categories")}
      </Button>
      {isPanelOpen && (
        <Box id={panelId} mt="2">
          {isSearching ? (
            <CpvSearchResults
              query={deferredQuery}
              tree={tree}
              selected={selectedSet}
              onToggleSelect={handleToggleSelect}
              onlyAdditional={onlyAdditional}
              onEscape={() => browseRef.current?.focus()}
            />
          ) : (
            <Box
              borderWidth="1px"
              borderColor="smBorder"
              borderRadius="md"
              p="2"
              bg="smBackground"
              maxH="24rem"
              overflowY="auto"
              // Chrome makes a scroll container keyboard-focusable, which lands a tab stop and a
              // focus ring on the box before the tree. The rows inside are reachable on their own
              // and scroll into view as focus moves, so the box needs no stop of its own.
              tabIndex={-1}
            >
              <CpvCategoryTree
                nodes={tree}
                selected={selectedSet}
                expandedIds={expandedIds}
                onToggleSelect={handleToggleSelect}
                onToggleExpand={handleToggleExpand}
                onlyAdditional={onlyAdditional}
                onEscape={() => browseRef.current?.focus()}
              />
            </Box>
          )}
        </Box>
      )}
      {suggestionContext && (
        <CpvSuggestions
          context={suggestionContext}
          selected={value}
          onAdd={(code) => {
            if (!selectedSet.has(code)) change([...value, code]);
          }}
          isDisabled={isDisabled || isLoading}
        />
      )}
    </Box>
  );
};
